import { useRef, useEffect } from "react"
import Modal from "shared/components/modal"
import classnames from "classnames"
import ErrorIcon from "shared/components/icons/error_icon"

import styles from "./generator_modal.module.scss"
import errorStyles from "../error_message.module.scss"

interface Props {
  askAItoGenerateFilterCode: () => void
  onClose: () => void
  prompt: string
  setPrompt: (value: string) => void
  showAIGenerator: boolean
  generatedCode: string
  isGenerating: boolean
  aiError: string
  rateLimited: boolean
  clearAiError: () => void
}

const AIGeneratorModal = (props: Props) => {
  const { askAItoGenerateFilterCode, onClose, prompt, setPrompt, showAIGenerator, generatedCode, isGenerating, aiError, rateLimited, clearAiError } = props
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const outputTextAreaRef = useRef<HTMLTextAreaElement>(null)

  const onCopyClick = () => {
    if (generatedCode) {
      navigator.clipboard.writeText(generatedCode)
        .then(() => {
          window.appendFlash("Copied to clipboard", "success")
          onClose()
        })
        .catch(error => {
          console.error("Clipboard write failed:", error)
          window.appendFlash("Could not copy automatically. Please copy the code manually from the editor.", "error")
        })
    }
  }

  // Focus on input textarea when modal opens
  useEffect(() => {
    if (showAIGenerator && textAreaRef.current) {
      textAreaRef.current.focus()
    }
  }, [showAIGenerator])

  return (
    <div className={styles["ai-generator-modal"]}>
      <Modal show={showAIGenerator} includeCloseX={false}>
        <h3>Generate filter code with AI</h3>
        <p>
          AI has access to the foundation of your Applet automations (triggers, queries, and actions.) Provide a clear prompt with the desired outcome to generate the filter code.
        </p>

        <div className={styles["form-container"]}>
          <div className={styles["connector-line"]} />

          {/* Input field */}
          <div className={styles["field-wrapper"]}>
            <div className={styles["field-container"]}>
              <label className={styles["field-label"]}>Prompt</label>
              <textarea
                ref={textAreaRef}
                className={styles["textarea"]}
                placeholder="Ex. If the trigger runs during my work hours, then skip the action"
                value={prompt}
                onChange={evt => {
                  setPrompt(evt.currentTarget.value)
                  if (aiError) clearAiError()
                }}
                onKeyDown={evt => {
                  if (evt.key === "Enter" && !evt.shiftKey && !isGenerating && !rateLimited && prompt.length > 0) {
                    evt.preventDefault()
                    askAItoGenerateFilterCode()
                  }
                }}
                readOnly={isGenerating || rateLimited}
                rows={3}
              />
            </div>
            <div className={styles["button-row"]}>
              <button
                className={classnames(
                  styles["generate-button"],
                  { [styles["disabled"]]: isGenerating || rateLimited || prompt.length === 0 }
                )}
                onClick={askAItoGenerateFilterCode}
                disabled={isGenerating || rateLimited || prompt.length === 0}
              >
                {isGenerating ? "Asking AI ..." : "Generate code"}
              </button>
            </div>
            {aiError && (
              <span className={errorStyles["error-message"]} style={{ marginTop: 16 }}>
                <ErrorIcon width="24" height="24" />
                <span>{aiError}</span>
              </span>
            )}
          </div>

          {/* Output field */}
          <div className={styles["field-wrapper"]}>
            <div className={styles["field-container"]}>
              <label className={styles["field-label"]}>Output</label>
              <textarea
                ref={outputTextAreaRef}
                className={classnames(
                  styles["textarea"],
                  styles["code-font"],
                  { [styles["loading"]]: isGenerating }
                )}
                placeholder="Generated code will display here."
                value={generatedCode}
                readOnly
                rows={4}
              />
            </div>
            <div className={styles["actions"]}>
              <button
                className={styles["close-button"]}
                onClick={onClose}
              >
                Close
              </button>
              <button
                className={classnames(
                  styles["copy-button"],
                  { [styles["disabled"]]: !generatedCode }
                )}
                onClick={onCopyClick}
                disabled={!generatedCode}
              >
                Copy to clipboard
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default AIGeneratorModal
